// This module properly logs and reports errors
import Server from './server/index';
import LocalStorage from './LocalStorage';
import UserAccount from './UserAccount';
import Constants from '../submodules/logictry_config/constants';
import Version from '../version.json';
import { getLast10RenderedComponents } from '../submodules/logictry_config/LogictryComponent';
import isCrawler from '../utils/isCrawler';
import { debugLog } from '../utils/debugLog';

const errors = new LocalStorage('logictry.errors');

const SuppressErrors = [
  `Failed to execute 'transaction' on 'IDBDatabase'`,
  'Object Not Found Matching Id:',
  'Cannot redefine property: googletag',
  'window.webkit.messageHandlers',
];
const SuppressStacks = [
  'chrome-extension://',
  'webkit-masked-url://',
  'moz-extension://',
  'https://accounts.google.com',
  'at UtilityScript',
  '@<anonymous code>',
  'at <anonymous code>',
  '@<anonymous>',
  'at <anonymous>',
  'global code@',
];

const ONE_HOUR_TIMEOUT = 60 * 60;

Object.keys(errors.state).forEach((key) => {
  if (typeof errors.state[key] !== 'object') return delete errors.state[key];

  const { lastUpdateTime, fatal } = errors.state[key];
  if (!lastUpdateTime) return null;

  const lastUpdate = parseInt(lastUpdateTime, 10);
  const delta = (Date.now() - lastUpdate) / 1000;

  if (delta > ONE_HOUR_TIMEOUT) delete errors.state[key];

  if (fatal && delta < 10) errors.state[key].count += 1;
  return null;
});
errors.saveState();

window.addEventListener('unhandledrejection', handleError);
window.addEventListener('error', handleError);

let _checkingReload = false;
let embedParentWebsite;

function clipUrl(value) {
  if (!value || typeof value !== 'string') return undefined;
  try {
    const parsed = new URL(value);
    const path = `${parsed.origin}${parsed.pathname}`;
    return path.length > 500 ? `${path.slice(0, 499)}…` : path;
  } catch {
    return undefined;
  }
}

function ancestorOriginList() {
  const list = window.location.ancestorOrigins;
  if (!list || !list.length) return undefined;
  return Array.from(list).slice(0, 5);
}

// The connect script posts the parent page URL. A cross-origin frame cannot read it itself.
// Keep it only when the message came from the parent window and the URL is on the sender's origin.
export function noteEmbedParent(url, senderOrigin, senderWindow) {
  if (typeof url !== 'string' || !url) return;
  if (senderWindow !== window.parent) return;
  try {
    if (new URL(url).origin !== senderOrigin) return;
    embedParentWebsite = clipUrl(url);
  } catch {
    // ignore a parent message that is not a URL
  }
}

function stackFingerprint(message, stack) {
  const head = stack && stack.split('\n', 3).join('\n');
  return head || String(message).slice(0, 200);
}

function isChunkLoadError(message, stack) {
  const text = `${message || ''}\n${stack || ''}`;
  return /Loading chunk \d+ failed/i.test(text) || /ChunkLoadError/i.test(text);
}

function reloadAfterChunkError() {
  if (_checkingReload) return;
  _checkingReload = true;
  setTimeout(() => {
    _checkingReload = false;
    if (sessionStorage.getItem('logictry_chunk_reload')) return;
    sessionStorage.setItem('logictry_chunk_reload', 'true');
    window.location.reload();
  });
}

function handleError(e) {
  if (!e) return;
  const error = e.error || e.reason;
  if (!error) return;
  const message = error.message || JSON.stringify(error);
  const stack = error.stack;
  const filename = e.filename || error.filename;

  setTimeout(() => {
    const isFatal = document.getElementById('root').children.length === 0 && document.readyState === 'complete';
    reportError(message, stack, isFatal, filename);
  });
}

export function reportError(message, stack, isFatal, filename) {
  if (isCrawler(navigator.userAgent)) return;

  if (isChunkLoadError(message, stack)) {
    reloadAfterChunkError();
    return;
  }

  if (!isFatal) {
    if (message && SuppressErrors.find((error) => message.includes(error))) return;
    if (filename && !filename.includes('logictry.com')) return;
    if (stack && SuppressStacks.find((s) => stack.includes(s))) return;
  }

  const errorKey = stack && stack.split('\n', 2).join('') || message;

  errors.state[errorKey] = errors.state[errorKey] || { startTime: Date.now(), count: 0 };
  const { count, lastUpdateTime } = errors.state[errorKey];

  errors.state[errorKey].fatal = errors.state[errorKey].fatal || isFatal;
  errors.saveState();

  const checkReload = () => {
    _checkingReload = false;
    if (isFatal && count < 3) window.location = window.location.origin;
  };

  if (lastUpdateTime) {
    const lastUpdate = parseInt(lastUpdateTime, 10);
    const delta = (Date.now() - lastUpdate) / 1000;
    if (delta < ONE_HOUR_TIMEOUT) {
      errors.state[errorKey].count = (errors.state[errorKey].count || 0) + 1;
      errors.saveState();
      if (!_checkingReload) {
        _checkingReload = true;
        setTimeout(checkReload);
      }
      return;
    }
  }

  errors.state[errorKey].lastUpdateTime = Date.now();
  errors.state[errorKey].count = (errors.state[errorKey].count || 0) + 1;
  errors.saveState();

  if (!isFatal && !stack) return;

  const { isWidget, isWidgetOnSameDomain } = Constants;
  const last10Rendered = getLast10RenderedComponents();
  const reportCount = errors.state[errorKey].count || 1;

  const payload = {
    kind: 'client_exception',
    fingerprint: stackFingerprint(message, stack),
    platform: Constants.isIOS ? 'ios' : Constants.isAndroid ? 'android' : 'web',
    appVersion: Version.version,
    timestamp: new Date().toISOString(),
    href: window.location.href,
    message,
    stack: stack ? `${stack}\n\n${last10Rendered}` : last10Rendered,
    fatal: !!isFatal,
    count: reportCount,
    extra: {
      filename,
      isWidget,
      isWidgetOnSameDomain,
      userAgent: navigator.userAgent,
      account: UserAccount.isLoggedIn ? UserAccount.account._id : null,
      ...(isWidget ? {
        documentReferrer: clipUrl(document.referrer),
        ancestorOrigins: ancestorOriginList(),
        parentWebsite: embedParentWebsite,
      } : {}),
    },
  };

  _checkingReload = true;

  debugLog('client_exception', payload.message, payload);
  if (Constants.isDevelopment) {
    window.alert(`${payload.message}\n\n${payload.stack}`);
    _checkingReload = false;
    return;
  }
  Promise.resolve(Server.postObservabilityEvent(payload))
    .then(checkReload)
    .catch(() => {
      _checkingReload = false;
    });
}
